import dayjs, {Dayjs} from 'dayjs';
import {RuleGroupTypeIC, RuleType} from 'react-querybuilder';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekday from 'dayjs/plugin/weekday';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import {v4 as uuidv4} from 'uuid';
import {t} from 'i18next';
import {FieldType} from '../components/pages/field-management/custom-field/constant';

dayjs.extend(isoWeek);
dayjs.extend(weekday);
dayjs.extend(isSameOrAfter);

export enum QBOperators {
  anyOfThese = 'Any of these',
  noneOfThese = 'None of these',
  anyOfTheseText = 'Any of these text',
  noneOfTheseText = 'None of these text',
  contains = 'Contains',
  doesNotContain = 'Does not contain',
  isSet = 'Is set',
  isNotSet = 'Is not set',
  is = 'Is',
  isNot = 'Is not',
  isNotSetText = 'Is not set text',
  isSetText = 'Is set text',
  isLessthan = 'Is less than',
  isGreaterThan = 'Is greater than',
  isLessThanOrEqualTo = 'Is less than or equal to',
  isBefore = 'Is before',
  isBeforeOrOn = 'Is before or on',
  isAfter = 'Is after',
  isAfterOrOn = 'Is after or on',
  isIn = 'Is in',
  isNotIn = 'Is not in',
  between = 'Between',
  beginsWith = 'Begins with',
  doesNotBeginWith = 'Does not begin with',
  endsWith = 'Ends with',
  doesNotEndWith = 'Does not end with',
  thisWeek = 'This Week',
  thisMonth = 'This Month',
  lastWeek = 'Last Week',
  last7Days = 'Last 7 Days',
  last30Days = 'Last 30 Days',
  month = 'Month',
  january = 'January',
  february = 'February',
  march = 'March',
  april = 'April',
  may = 'May',
  june = 'June',
  july = 'July',
  august = 'August',
  september = 'September',
  october = 'October',
  november = 'November',
  december = 'December',
}

export enum QBDropTypes {
  GROUP = 'GROUP',
  TAG = 'TAG',
  EXPORT = 'EXPORT',
  SAVED_SEARCH = 'SAVED_SEARCH',
  STANDARED_METADATA_FIELD = 'STANDARED_METADATA_FIELD',
  CUSTOM_METADATA_FIELD = 'CUSTOM_METADATA_FIELD',
  DEFAULT_FIELD = 'DEFAULT_FIELD',
}

export enum QBCombinators {
  AND = 'and',
  OR = 'or',
  NOT = 'not',
}

export interface IQueryFieldOperator {
  name: QBOperators;
  label: string;
}

export const getNumberOperators = (): IQueryFieldOperator[] => [
  {name: QBOperators.is, label: t('queryBuilderOperators.is')},
  {name: QBOperators.isNot, label: t('queryBuilderOperators.isNot')},
  {name: QBOperators.isSetText, label: t('queryBuilderOperators.isSet')},
  {name: QBOperators.isNotSetText, label: t('queryBuilderOperators.isNotSet')},
  {name: QBOperators.isLessthan, label: t('queryBuilderOperators.isLessThan')},
  {
    name: QBOperators.isGreaterThan,
    label: t('queryBuilderOperators.isGreaterThan'),
  },
];

export const getKeywordOperators = (): IQueryFieldOperator[] => [
  {name: QBOperators.is, label: t('queryBuilderOperators.is')},
  {name: QBOperators.isNot, label: t('queryBuilderOperators.isNot')},
  {name: QBOperators.isSet, label: t('queryBuilderOperators.isSet')},
  {name: QBOperators.isNotSet, label: t('queryBuilderOperators.isNotSet')},
  {name: QBOperators.contains, label: t('queryBuilderOperators.contains')},
  {
    name: QBOperators.doesNotContain,
    label: t('queryBuilderOperators.doesNotContain'),
  },
  {name: QBOperators.beginsWith, label: t('queryBuilderOperators.beginsWith')},
  {
    name: QBOperators.doesNotBeginWith,
    label: t('queryBuilderOperators.doesNotBeginWith'),
  },
];

export const getTextOperators = (): IQueryFieldOperator[] => [
  {name: QBOperators.isIn, label: t('queryBuilderOperators.is')},
  {name: QBOperators.isNotIn, label: t('queryBuilderOperators.isNot')},
  {name: QBOperators.isSetText, label: t('queryBuilderOperators.isSet')},
  {name: QBOperators.isNotSetText, label: t('queryBuilderOperators.isNotSet')},
  {name: QBOperators.contains, label: t('queryBuilderOperators.contains')},
  {
    name: QBOperators.doesNotContain,
    label: t('queryBuilderOperators.doesNotContain'),
  },
  {name: QBOperators.beginsWith, label: t('queryBuilderOperators.beginsWith')},
  {
    name: QBOperators.doesNotBeginWith,
    label: t('queryBuilderOperators.doesNotBeginWith'),
  },
];

export const getDateAndTimeOperators = (): IQueryFieldOperator[] => [
  {name: QBOperators.is, label: t('queryBuilderOperators.is')},
  {name: QBOperators.isNot, label: t('queryBuilderOperators.isNot')},
  {name: QBOperators.isSetText, label: t('queryBuilderOperators.isSet')},
  {name: QBOperators.isNotSetText, label: t('queryBuilderOperators.isNotSet')},
  {name: QBOperators.isBefore, label: t('queryBuilderOperators.isBeforeOrOn')},
  {name: QBOperators.isAfter, label: t('queryBuilderOperators.isAfterOrOn')},
  {name: QBOperators.between, label: t('queryBuilderOperators.between')},
  {name: QBOperators.isIn, label: t('queryBuilderOperators.isIn')},
];

export const getIsInOperators = (): IQueryFieldOperator[] => [
  {name: QBOperators.thisWeek, label: t('queryBuilderOperators.thisWeek')},
  {name: QBOperators.thisMonth, label: t('queryBuilderOperators.thisMonth')},
  {name: QBOperators.lastWeek, label: t('queryBuilderOperators.lastWeek')},
  {name: QBOperators.last7Days, label: t('queryBuilderOperators.last7Days')},
  {name: QBOperators.last30Days, label: t('queryBuilderOperators.last30Days')},
  {name: QBOperators.month, label: t('queryBuilderOperators.month')},
];

export const getMonthOperators = (): IQueryFieldOperator[] => [
  {name: QBOperators.january, label: t('queryBuilderOperators.january')},
  {name: QBOperators.february, label: t('queryBuilderOperators.february')},
  {name: QBOperators.march, label: t('queryBuilderOperators.march')},
  {name: QBOperators.april, label: t('queryBuilderOperators.april')},
  {name: QBOperators.may, label: t('queryBuilderOperators.may')},
  {name: QBOperators.june, label: t('queryBuilderOperators.june')},
  {name: QBOperators.july, label: t('queryBuilderOperators.july')},
  {name: QBOperators.august, label: t('queryBuilderOperators.august')},
  {name: QBOperators.september, label: t('queryBuilderOperators.september')},
  {name: QBOperators.october, label: t('queryBuilderOperators.october')},
  {name: QBOperators.november, label: t('queryBuilderOperators.november')},
  {name: QBOperators.december, label: t('queryBuilderOperators.december')},
];

export const getGeneralOperators = (): IQueryFieldOperator[] => [
  {name: QBOperators.isIn, label: t('queryBuilderOperators.isIn')},
  {name: QBOperators.isNotIn, label: t('queryBuilderOperators.isNotIn')},
];

export const getFileTransferListOperators = (
  fieldType: FieldType
): IQueryFieldOperator[] => [
  {
    name:
      fieldType === 'Keyword'
        ? QBOperators.anyOfThese
        : QBOperators.anyOfTheseText,
    label: t('queryBuilderOperators.anyOfThese'),
  },
  {
    name:
      fieldType === 'Keyword'
        ? QBOperators.noneOfThese
        : QBOperators.noneOfTheseText,
    label: t('queryBuilderOperators.noneOfThese'),
  },
  {
    name: fieldType === 'Keyword' ? QBOperators.isSet : QBOperators.isSetText,
    label: t('queryBuilderOperators.isSet'),
  },
  {
    name:
      fieldType === 'Keyword' ? QBOperators.isNotSet : QBOperators.isNotSetText,
    label: t('queryBuilderOperators.isNotSet'),
  },
];

export const jsonLogicOperatorMap: Record<string, string> = {
  [QBOperators.anyOfThese]: '=',
  [QBOperators.noneOfThese]: '!=',
  [QBOperators.anyOfTheseText]: 'in',
  [QBOperators.noneOfTheseText]: 'notIn',
  [QBOperators.isSet]: '=',
  [QBOperators.isNotSet]: '!=',
  [QBOperators.isNotSetText]: 'notIn',
  [QBOperators.isSetText]: 'in',

  [QBOperators.isNot]: '!=',
  [QBOperators.isLessthan]: '<',
  [QBOperators.isGreaterThan]: '>',
  [QBOperators.isLessThanOrEqualTo]: '<=',
  [QBOperators.isBefore]: '<',
  [QBOperators.isBeforeOrOn]: '<=',
  [QBOperators.isAfter]: '>',
  [QBOperators.isAfterOrOn]: '>=',
  [QBOperators.between]: 'in',
  [QBOperators.is]: '=',

  [QBOperators.isIn]: 'in',
  [QBOperators.isNotIn]: 'notIn',
  [QBOperators.contains]: 'contains',
  [QBOperators.doesNotContain]: 'notIn',
  [QBOperators.beginsWith]: 'beginsWith',
  [QBOperators.doesNotBeginWith]: 'doesNotBeginWith',
  [QBOperators.endsWith]: 'endsWith',
  [QBOperators.doesNotEndWith]: 'doesNotEndWith',
};

type RuleIC = string | RuleType | RuleGroupTypeIC;

const isValueWithinPeriodOperators = (value: QBOperators) =>
  [
    QBOperators.thisWeek,
    QBOperators.thisMonth,
    QBOperators.lastWeek,
    QBOperators.last7Days,
    QBOperators.last30Days,
    QBOperators.january,
    QBOperators.february,
    QBOperators.march,
    QBOperators.april,
    QBOperators.may,
    QBOperators.june,
    QBOperators.july,
    QBOperators.august,
    QBOperators.september,
    QBOperators.october,
    QBOperators.november,
    QBOperators.december,
  ].includes(value);

export const getPeriodDateRange = (period?: QBOperators) => {
  const today = dayjs();
  let start: Dayjs = dayjs(),
    end: Dayjs = dayjs();

  switch (period) {
    case QBOperators.thisWeek: {
      start = today.startOf('week');
      end = today.endOf('week');
      break;
    }
    case QBOperators.thisMonth: {
      start = today.startOf('month');
      end = today.endOf('month');
      break;
    }
    case QBOperators.lastWeek: {
      const lastWeekStart = today.subtract(1, 'week').startOf('week');
      start = lastWeekStart;
      end = lastWeekStart.endOf('week');
      break;
    }
    case QBOperators.last7Days: {
      start = today.subtract(6, 'day').startOf('day');
      end = today.endOf('day');
      break;
    }
    case QBOperators.last30Days: {
      start = today.subtract(29, 'day').startOf('day');
      end = today.endOf('day');
      break;
    }
    case QBOperators.january:
    case QBOperators.february:
    case QBOperators.march:
    case QBOperators.april:
    case QBOperators.may:
    case QBOperators.june:
    case QBOperators.july:
    case QBOperators.august:
    case QBOperators.september:
    case QBOperators.october:
    case QBOperators.november:
    case QBOperators.december: {
      const inputMonth = dayjs(period, 'MMMM');
      if (today.isBefore(inputMonth.startOf('month'))) {
        const target = inputMonth.subtract(1, 'year');
        start = target.startOf('month');
        end = target.endOf('month');
      } else {
        start = inputMonth.startOf('month');
        end = inputMonth.endOf('month');
      }
      break;
    }
    default:
      return [];
  }
  return [start.format('YYYY/MM/DD'), end.format('YYYY/MM/DD')];
};

export function convertBetweenInRuleGroup(
  query: RuleGroupTypeIC
): RuleGroupTypeIC {
  const process = (group: RuleGroupTypeIC): RuleGroupTypeIC => {
    const newRules: RuleIC[] = [];

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const [index, rule] of group.rules.entries()) {
      if (typeof rule === 'string') {
        newRules.push(rule);
      } else if ('rules' in rule) {
        newRules.push(process(rule));
      } else {
        const r = rule as RuleType;

        // Handle "between" conversion
        if (
          r.operator === QBOperators.between &&
          typeof r.value === 'string' &&
          r.value.includes(' - ')
        ) {
          newRules.push({
            field: r.field,
            operator: 'between',
            value: r.value.replace(' - ', ','),
          });
        } else if (
          r.operator === QBOperators.isIn &&
          isValueWithinPeriodOperators(r.value)
        ) {
          const startEndDate = getPeriodDateRange(r.value).join(' TO ');
          newRules.push({
            field: r.field,
            operator: 'in',
            value: [startEndDate],
          });

          // Handle wrapping in negated rule group
        } else if (
          [
            QBOperators.isNot as string,
            QBOperators.doesNotBeginWith as string,
            QBOperators.isNotSet as string,
            QBOperators.isNotIn as string,
            QBOperators.isNotSetText as string,
            QBOperators.doesNotContain as string,
          ].includes(r.operator as string)
        ) {
          newRules.push({
            rules: [r],
            id: uuidv4(),
          } as RuleGroupTypeIC); // Typecast to RuleGroup if needed
        } else if (
          jsonLogicOperatorMap[r.operator] == 'in' &&
          r.value.includes(',')
        ) {
          newRules.push({
            field: r.field,
            operator: r.operator,
            value: r.value.replace(',', '\\,'),
          });
        } else {
          newRules.push(r);
        }
      }
    }

    return {
      ...group,
      rules: newRules as RuleGroupTypeIC['rules'],
    };
  };

  return process(query);
}
